import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import { ClientApartment, AptPackage, getPublicAptPackages } from "../../utils/api/apartmentRentalsAPI";
import { usePublicLang } from "./PublicLangContext";
import { LangSwitcher } from "./LangSwitcher";
import "./PublicApartments.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(n)}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(n)} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(n)} RSD`;
};

const todayStr = () => new Date().toISOString().split("T")[0];

const isPackageActive = (pkg: AptPackage) => {
  const today = new Date().toISOString().split("T")[0];
  return pkg.isActive && pkg.validFrom <= today && pkg.validTo >= today;
};


const computeEstimatedTotal = (apt: ClientApartment, ci: string, co: string): number => {
  if (!ci || !co || ci >= co) return 0;
  const rules = apt.pricingRules || [];
  let total = 0;
  let current = new Date(ci + "T12:00:00.000Z");
  const end = new Date(co + "T12:00:00.000Z");
  while (current < end) {
    const dateStr = current.toISOString().split("T")[0];
    let price = apt.pricePerNight;
    for (const rule of rules) {
      if (dateStr >= rule.from && dateStr <= rule.to) {
        price = rule.type === "custom" ? rule.value : apt.pricePerNight * (1 - rule.value / 100);
        break;
      }
    }
    total += price;
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return Math.round(total);
};

const APT_TYPE_LABEL: Record<string, string> = {
  studio: "Studio", apartment: "Apartman", loft: "Loft", villa: "Vila", house: "Kuća",
};

const AMENITY_ICONS: Record<string, string> = {
  WiFi: "📶", Parking: "🅿️", "Air Conditioning": "❄️", Heating: "🔥",
  Kitchen: "🍳", "Washing Machine": "🌀", Balcony: "🏙", Pool: "🏊",
  Gym: "💪", TV: "📺", Dishwasher: "🫧", BBQ: "🔥",
};

const AVATAR_COLORS = [
  "#264a3a","#0e7490","#b45309","#3f6212","#9d3b2e","#4c3b6e","#0284c7","#a16207",
];

// ─── Module-level availability cache ─────────────────────────────────────────
const availCache = new Map<string, Record<number, string>>();

// ─── MiniCalendar ─────────────────────────────────────────────────────────────
const MiniCalendar: React.FC<{ aptId: string; checkIn: string; checkOut: string; viewYear: number; viewMonth: number; dowLabels: string[]; freeLabel: string; occupiedLabel: string }> = ({ aptId, checkIn, checkOut, viewYear, viewMonth, dowLabels, freeLabel, occupiedLabel }) => {
  const now = new Date();
  const [dayMap, setDayMap] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const key = `${aptId}-${viewYear}-${viewMonth}`;
    if (availCache.has(key)) {
      setDayMap(availCache.get(key)!);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get<{ dayMap: Record<number, string> }>(
        `${BASE_API_URL}/public/apartments/${aptId}/availability`,
        { params: { year: viewYear, month: viewMonth } }
      )
      .then((res) => {
        if (cancelled) return;
        const dm = res.data.dayMap || {};
        availCache.set(key, dm);
        setDayMap(dm);
      })
      .catch(() => {
        if (!cancelled) { availCache.set(key, {}); setDayMap({}); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [aptId, viewYear, viewMonth]);

  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const firstDow = (new Date(viewYear, viewMonth - 1, 1).getDay() + 6) % 7;

  const todayY = now.getFullYear();
  const todayM = now.getMonth() + 1;
  const todayD = now.getDate();

  const getDayClasses = (d: number): string => {
    const isPast =
      viewYear < todayY ||
      (viewYear === todayY && viewMonth < todayM) ||
      (viewYear === todayY && viewMonth === todayM && d < todayD);
    if (isPast) return "pa-cal__day--past";

    const status = dayMap[d];
    if (status === "confirmed" || status === "pending" || status === "blocked") return "pa-cal__day--busy";
    if (status === "closed") return "pa-cal__day--closed";
    if (status === "event") return "pa-cal__day--event";

    const thisStr = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (checkIn && checkOut) {
      if (thisStr === checkIn) return "pa-cal__day--sel pa-cal__day--start";
      if (thisStr === checkOut) return "pa-cal__day--sel pa-cal__day--end";
      if (thisStr > checkIn && thisStr < checkOut) return "pa-cal__day--range";
    } else if (checkIn && thisStr === checkIn) {
      return "pa-cal__day--sel";
    }

    const isToday = viewYear === todayY && viewMonth === todayM && d === todayD;
    return isToday ? "pa-cal__day--today" : "pa-cal__day--free";
  };

  return (
    <div className="pa-cal" onClick={(e) => e.stopPropagation()}>
      <div className="pa-cal__legend">
        <span><i className="pa-cal__lg pa-cal__lg--free" />{freeLabel}</span>
        <span><i className="pa-cal__lg pa-cal__lg--busy" />{occupiedLabel}</span>
      </div>
      {loading ? (
        <div className="pa-cal__loading">Učitavanje…</div>
      ) : (
        <div className="pa-cal__grid">
          {dowLabels.map((d, i) => <div key={i} className="pa-cal__dow">{d}</div>)}
          {Array.from({ length: firstDow }, (_, i) => (
            <div key={`e${i}`} className="pa-cal__day pa-cal__day--empty" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const d = i + 1;
            return <div key={d} className={`pa-cal__day ${getDayClasses(d)}`}>{d}</div>;
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PublicApartments: React.FC = () => {
  const { t, lang } = usePublicLang();
  const DOW_MINI: string[] = t("dowMini");
  const shortMonths: string[] = t("shortMonths");

  const fmtDisplayDate = (s: string) => {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return `${parseInt(d, 10)}. ${shortMonths[parseInt(m, 10) - 1]} ${y}`;
  };

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("clientId") || "";
  const refCode = params.get("ref") || "";

  const [apartments, setApartments] = useState<ClientApartment[]>([]);
  const [packages, setPackages] = useState<AptPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("sve");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const nowCal = new Date();
  const [calViewYear, setCalViewYear] = useState(nowCal.getFullYear());
  const [calViewMonth, setCalViewMonth] = useState(nowCal.getMonth() + 1);

  const navCalMonth = (dir: number) => {
    let m = calViewMonth + dir, y = calViewYear;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setCalViewMonth(m); setCalViewYear(y);
  };

  const calIsPrevDisabled = calViewYear < nowCal.getFullYear() || (calViewYear === nowCal.getFullYear() && calViewMonth <= nowCal.getMonth() + 1);
  const [totalCount, setTotalCount] = useState(0);
  const ciRef = useRef<HTMLInputElement>(null);
  const coRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchApartments = (ci?: string, co?: string) => {
    if (!clientId) return;
    const p: Record<string, string> = { clientId };
    if (ci && co && ci < co) { p.checkIn = ci; p.checkOut = co; }
    return axios.get<ClientApartment[]>(`${BASE_API_URL}/public/apartments`, { params: p });
  };

  const handlePullRefresh = useCallback(async () => {
    const res = await fetchApartments(
      dateFilterActive ? checkIn : undefined,
      dateFilterActive ? checkOut : undefined
    )!;
    setApartments(res.data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, dateFilterActive]);

  useEffect(() => {
    if (!clientId) {
      setError("Nedostaje clientId parametar u URL-u");
      setLoading(false);
      return;
    }
    fetchApartments()!
      .then((res) => { setApartments(res.data); setTotalCount(res.data.length); })
      .catch(() => setError("Greška pri učitavanju apartmana"))
      .finally(() => setLoading(false));
    getPublicAptPackages(clientId)
      .then((res) => setPackages(res.data.filter(isPackageActive)))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDateSearch = async (ci: string, co: string) => {
    if (!ci || !co || ci >= co) return;
    setSearching(true);
    setDateFilterActive(true);
    try {
      const res = await fetchApartments(ci, co)!;
      setApartments(res.data);
    } catch { /**/ } finally { setSearching(false); }
  };

  const clearDateFilter = async () => {
    setCheckIn(""); setCheckOut(""); setDateFilterActive(false);
    setSearching(true);
    try {
      const res = await fetchApartments()!;
      setApartments(res.data);
    } catch { /**/ } finally { setSearching(false); }
  };

  const handleCheckInChange = (v: string) => {
    setCheckIn(v);
    if (checkOut && v >= checkOut) setCheckOut("");
    if (v) setTimeout(() => coRef.current?.showPicker?.(), 120);
  };

  const handleCheckOutChange = (v: string) => {
    setCheckOut(v);
    if (checkIn && v > checkIn) {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => handleDateSearch(checkIn, v), 300);
    }
  };

  const handleSearch = () => {
    if (checkIn && checkOut && checkIn < checkOut) handleDateSearch(checkIn, checkOut);
  };

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    apartments.forEach((a) => a.tags?.forEach((t) => tags.add(t)));
    return Array.from(tags);
  }, [apartments]);

  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    apartments.forEach((a) => a.tags?.forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return counts;
  }, [apartments]);

  const filtered = useMemo(() => {
    let list = apartments;
    if (activeFilter !== "sve") list = list.filter((a) => a.tags?.includes(activeFilter));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.location.toLowerCase().includes(q) ||
          a.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [apartments, activeFilter, search]);

  const goToApartment = (id: string) => {
    const base = `/apartments/public/${id}?clientId=${clientId}`;
    const qs = checkIn && checkOut ? `&checkIn=${checkIn}&checkOut=${checkOut}` : "";
    const refQs = refCode ? `&ref=${refCode}` : "";
    navigate(base + qs + refQs);
  };

  const nights =
    checkIn && checkOut && checkIn < checkOut
      ? Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000)
      : 0;

  const heroImage = apartments.find(a => a.images?.length > 0)?.images[0];

  if (loading) {
    return (
      <div className="pa">
        <nav className="pa-nav">
          <span className="pa-nav__brand">
            <span className="pa-nav__logo" style={{ background: "linear-gradient(135deg, var(--pa-accent), var(--pa-accent2))" }}>🏠</span>
            <span className="pa-nav__name">Naši apartmani</span>
          </span>
        </nav>
        <div className="pa-loading"><span style={{ fontSize: 32 }}>🏠</span>{lang === "en" ? "Loading apartments..." : "Učitavanje apartmana..."}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pa">
        <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",padding:24,textAlign:"center" }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>🔗</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--pa-ink)", marginBottom: 6 }}>Stranica nije dostupna</div>
          <div style={{ fontSize: 14, color: "var(--pa-muted)" }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pa">

      {/* ── Navbar ── */}
      <nav className="pa-nav">
        <span className="pa-nav__brand">
          <span
            className="pa-nav__logo"
            style={{ background: apartments[0]?.coverGradient || "linear-gradient(135deg, var(--pa-accent), var(--pa-accent2))" }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>
            </svg>
          </span>
          <span className="pa-nav__name">{lang === "en" ? "Our apartments" : "Naši apartmani"}</span>
        </span>
        <div className="pa-nav__meta" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="pa-nav__count">
            <span className="pa-nav__dot" />
            {lang === "en"
              ? `${totalCount} apartment${totalCount === 1 ? "" : "s"} available`
              : `${totalCount} apartman${totalCount === 1 ? "" : totalCount >= 2 && totalCount <= 4 ? "a" : "a"} dostupno`}
          </span>
          <LangSwitcher />
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pa-hero">
        {heroImage ? (
          <div className="pa-hero__bg" style={{ backgroundImage: `url(${heroImage})` }} />
        ) : (
          <div className="pa-hero__bg pa-hero__bg--gradient" />
        )}
        <div className="pa-hero__inner">
          <span className="pa-hero__eyebrow">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 20l6-12 5 8 3-5 4 9z"/>
            </svg>
            {lang === "en" ? "Find the perfect accommodation" : "Pronađite savršen smještaj"}
          </span>
          <h1 className="pa-hero__title">
            {lang === "en" ? <>Book an apartment<br className="pa-hero__br"/>in nature</> : <>Rezervišite apartman<br className="pa-hero__br"/>u prirodi</>}
          </h1>
          <p className="pa-hero__sub">
            {lang === "en"
              ? `${totalCount} comfortable apartment${totalCount === 1 ? "" : "s"} · book online`
              : `${totalCount} udobn${totalCount === 1 ? "i" : "ih"} apartman${totalCount === 1 ? "" : "a"} · rezervišite online`}
          </p>
        </div>
      </section>

      {/* ── Floating search card ── */}
      <div className="pa-search-wrap">
        <div className="pa-search">
          {/* Check-in */}
          <div
            className={`pa-search__field${checkIn ? " pa-search__field--filled" : ""}`}
            onClick={() => ciRef.current?.showPicker?.()}
          >
            <span className="pa-search__label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/>
              </svg>
              {t("checkIn")}
            </span>
            <span className={`pa-search__val${!checkIn ? " placeholder" : ""}`}>
              {checkIn ? fmtDisplayDate(checkIn) : (lang === "en" ? "Select date" : "Odaberite datum")}
            </span>
            <input
              ref={ciRef}
              type="date"
              value={checkIn}
              min={todayStr()}
              onChange={(e) => handleCheckInChange(e.target.value)}
              className="pa-search__hidden-input"
            />
          </div>

          <div className="pa-search__arrow">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M5 12h14M13 6l6 6-6 6"/>
            </svg>
          </div>

          {/* Check-out */}
          <div
            className={`pa-search__field${checkOut ? " pa-search__field--filled" : ""}${!checkIn ? " pa-search__field--disabled" : ""}`}
            onClick={() => checkIn && coRef.current?.showPicker?.()}
          >
            <span className="pa-search__label">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/>
              </svg>
              {t("checkOut")}
            </span>
            <span className={`pa-search__val${!checkOut ? " placeholder" : ""}`}>
              {checkOut ? fmtDisplayDate(checkOut) : (lang === "en" ? "Select date" : "Odaberite datum")}
            </span>
            <input
              ref={coRef}
              type="date"
              value={checkOut}
              min={checkIn || todayStr()}
              onChange={(e) => handleCheckOutChange(e.target.value)}
              className="pa-search__hidden-input"
              disabled={!checkIn}
            />
          </div>

          {/* Actions */}
          {dateFilterActive && (
            <button className="pa-search__clear" onClick={clearDateFilter}>{lang === "en" ? "Clear" : "Poništi"}</button>
          )}
          <button className="pa-search__go" onClick={handleSearch}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.4-4.4"/>
            </svg>
            {lang === "en" ? "Search" : "Pretraži"}
          </button>
        </div>

        {/* Nights hint */}
        {nights > 0 && (
          <div className="pa-search__hint">
            🌙 {nights} {lang === "en" ? (nights === 1 ? "night" : "nights") : (nights === 1 ? "noć" : "noći")} · {fmtDisplayDate(checkIn)} → {fmtDisplayDate(checkOut)}
          </div>
        )}
      </div>

      {/* ── Page body ── */}
      <div className="pa-body">
        <div className="pa-container">

          {/* Text search */}
          <div className="pa-text-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.4-4.4"/>
            </svg>
            <input
              type="text"
              placeholder={lang === "en" ? "Search by name or location…" : "Pretražite po imenu ili lokaciji…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch("")} className="pa-text-search__clear">✕</button>
            )}
          </div>

          {/* Filter chips */}
          {allTags.length > 0 && (
            <div className="pa-filters">
              <button
                className={`pa-chip${activeFilter === "sve" ? " pa-chip--active" : ""}`}
                onClick={() => setActiveFilter("sve")}
              >
                {lang === "en" ? "All" : "Svi"} <span className="pa-chip__cnt">{apartments.length}</span>
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  className={`pa-chip${activeFilter === tag ? " pa-chip--active" : ""}`}
                  onClick={() => setActiveFilter(tag)}
                >
                  {tag} <span className="pa-chip__cnt">{tagCounts[tag] || 0}</span>
                </button>
              ))}
            </div>
          )}

          {/* Availability banner */}
          {dateFilterActive && !searching && (
            <div className="pa-avail-banner">
              <span className="ic">✅</span>
              {lang === "en"
                ? <><strong>{filtered.length}</strong> available · {fmtDisplayDate(checkIn)} → {fmtDisplayDate(checkOut)} · {nights} {nights === 1 ? "night" : "nights"}</>
                : <><strong>{filtered.length}</strong> slobodn{filtered.length === 1 ? "o" : "ih"} · {fmtDisplayDate(checkIn)} → {fmtDisplayDate(checkOut)} · {nights} noć{nights === 1 ? "" : "i"}</>
              }
              <button className="pa-avail-clear" onClick={clearDateFilter}>{lang === "en" ? "Show all" : "Prikaži sve"}</button>
            </div>
          )}
          {searching && (
            <div className="pa-avail-banner pa-avail-banner--loading">
              ⏳ {lang === "en" ? "Searching available apartments..." : "Pretraživanje slobodnih apartmana..."}
            </div>
          )}

          {/* Results meta */}
          <div className="pa-results-meta">
            <strong>{filtered.length}</strong>{" "}
            {lang === "en" ? (filtered.length === 1 ? "apartment" : "apartments") : (filtered.length === 1 ? "apartman" : "apartmana")}
            {activeFilter !== "sve" ? ` · ${activeFilter}` : ""}
            {checkIn && checkOut ? ` · ${fmtDisplayDate(checkIn)} – ${fmtDisplayDate(checkOut)}` : ""}
          </div>

          {/* Shared calendar navigator */}
          {filtered.length > 0 && !searching && (
            <div className="pa-cal-nav-shared" onClick={(e) => e.stopPropagation()}>
              <button
                className="pa-cal-nav-shared__btn"
                disabled={calIsPrevDisabled}
                onClick={() => navCalMonth(-1)}
              >‹</button>
              <span className="pa-cal-nav-shared__month">
                {t("monthNames")[calViewMonth - 1]} {calViewYear}
              </span>
              <button
                className="pa-cal-nav-shared__btn"
                onClick={() => navCalMonth(1)}
              >›</button>
            </div>
          )}

          {/* Grid */}
          {searching ? (
            <div className="pa-loading" style={{ minHeight: 180 }}>
              <span style={{ fontSize: 28 }}>🔍</span>{lang === "en" ? "Searching..." : "Pretraživanje..."}
            </div>
          ) : filtered.length === 0 ? (
            <div className="pa-empty">
              <div className="icon">{dateFilterActive ? "📅" : "🏠"}</div>
              <div className="t">{dateFilterActive ? t("noApartmentsAvailable") : t("noResults")}</div>
              <div className="s">
                {dateFilterActive
                  ? (lang === "en" ? "All apartments are occupied for the selected period. Try different dates." : "Svi apartmani su zauzeti u odabranom periodu. Probajte drugi termin.")
                  : (lang === "en" ? "Try a different search or filter" : "Pokušajte drugačiju pretragu ili filter")}
              </div>
              {dateFilterActive && (
                <button className="pa-cta-btn" style={{ marginTop: 16 }} onClick={clearDateFilter}>
                  {lang === "en" ? "Show all apartments" : "Prikaži sve apartmane"}
                </button>
              )}
            </div>
          ) : (
            <div className="pa-grid">
              {filtered.map((apt, i) => {
                const total = computeEstimatedTotal(apt, checkIn, checkOut);

                const bestDiscount = Math.max(
                  (apt.lastMinute?.enabled ? apt.lastMinute.discount : 0) ?? 0,
                  (apt.earlyBooking?.enabled ? apt.earlyBooking.discount : 0) ?? 0,
                );
                const discountedNight = bestDiscount > 0
                  ? Math.round(apt.pricePerNight * (1 - bestDiscount / 100))
                  : null;

                return (
                  <article key={apt._id} className="pa-card" onClick={() => goToApartment(apt._id)}>
                    {/* Cover */}
                    <div className="pa-card-cover">
                      {apt.images?.length > 0 ? (
                        <img src={apt.images[0]} alt={apt.name} loading="lazy" />
                      ) : (
                        <div
                          className="pa-card-cover__gradient"
                          style={{
                            background: apt.coverGradient ||
                              `linear-gradient(135deg, ${AVATAR_COLORS[i % AVATAR_COLORS.length]}, ${AVATAR_COLORS[(i + 3) % AVATAR_COLORS.length]})`,
                          }}
                        />
                      )}
                      <div className="pa-card-cover__grad" />

                      {apt.tags?.length > 0 && (
                        <div className="pa-card-cover__tags">
                          {apt.tags.slice(0, 2).map((t) => (
                            <span key={t} className="pa-card-cover__tag">{t}</span>
                          ))}
                        </div>
                      )}

                      <div className="pa-card-cover__price">
                        {discountedNight ? (
                          <>
                            <span className="from">od</span>
                            <span className="amt">{fmtAmount(discountedNight, apt.currency)}</span>
                          </>
                        ) : (
                          <span className="amt">{fmtAmount(apt.pricePerNight, apt.currency)}</span>
                        )}
                        <span className="per">/ {lang === "en" ? "night" : "noć"}</span>
                      </div>

                      <div className="pa-card-cover__name">
                        <div className="nm">{apt.name}</div>
                        <div className="loc">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                            <circle cx="12" cy="10" r="3"/><path d="M12 21s7-7 7-11a7 7 0 0 0-14 0c0 4 7 11 7 11z"/>
                          </svg>
                          {apt.location}
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="pa-card-body">
                      <div className="pa-card-meta">
                        <span className="pa-card-meta__item">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                            <circle cx="9" cy="8" r="3"/><path d="M2 20a7 7 0 0 1 14 0"/><path d="M16 5a3 3 0 0 1 0 6"/>
                          </svg>
                          {apt.capacity} {lang === "en" ? "guests" : "gostiju"}
                        </span>
                        <span className="pa-card-meta__item">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                            <path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/>
                          </svg>
                          {APT_TYPE_LABEL[apt.type] || apt.type}
                        </span>
                      </div>

                      {apt.amenities?.length > 0 && (
                        <div className="pa-card-amenities">
                          {apt.amenities.slice(0, 4).map((a) => (
                            <span key={a} className="pa-card-amenity">
                              {AMENITY_ICONS[a] || "✓"} {a}
                            </span>
                          ))}
                          {apt.amenities.length > 4 && (
                            <span className="pa-card-amenity pa-card-amenity--more">
                              +{apt.amenities.length - 4}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Mini availability calendar */}
                      <MiniCalendar aptId={apt._id} checkIn={checkIn} checkOut={checkOut} viewYear={calViewYear} viewMonth={calViewMonth} dowLabels={DOW_MINI} freeLabel={t("free")} occupiedLabel={t("occupied")} />
                    </div>

                    {/* Footer */}
                    <div className="pa-card-foot">
                      <div className="pa-card-foot__info">
                        {nights > 0 && total > 0 ? (
                          <>
                            <div className="pa-card-foot__price">
                              {fmtAmount(total, apt.currency)}
                              <span className="per"> · {nights} {lang === "en" ? (nights === 1 ? "night" : "nights") : (nights === 1 ? "noć" : "noći")}</span>
                            </div>
                            {dateFilterActive ? (
                              <div className="pa-card-foot__note pa-card-foot__note--ok">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                  <path d="M20 6L9 17l-5-5"/>
                                </svg>
                                {lang === "en" ? "Available for your dates" : "Slobodno za vaše datume"}
                              </div>
                            ) : (
                              <div className="pa-card-foot__note">
                                {lang === "en" ? "Estimated total" : "Procijenjeno ukupno"}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="pa-card-foot__price">
                              {discountedNight ? (
                                <>
                                  <span className="pa-card-foot__discounted">{fmtAmount(discountedNight, apt.currency)}</span>
                                  <span className="pa-card-foot__range-sep">–</span>
                                  <span className="pa-card-foot__max">{fmtAmount(apt.pricePerNight, apt.currency)}</span>
                                </>
                              ) : (
                                fmtAmount(apt.pricePerNight, apt.currency)
                              )}
                              <span className="per"> / {lang === "en" ? "night" : "noć"}{(apt.minNights ?? 1) > 1 ? ` · min ${apt.minNights} ${lang === "en" ? "nights" : "noći"}` : ""}</span>
                            </div>
                            <div className="pa-card-foot__note">
                              {discountedNight
                                ? (lang === "en" ? "Price depends on booking dates" : "Cijena zavisi od termina rezervacije")
                                : (lang === "en" ? "Select dates for total price" : "Izaberite datume za ukupnu cijenu")}
                            </div>
                          </>
                        )}
                      </div>
                      <button className="pa-card-foot__btn">
                        {t("book")}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M5 12h14M13 6l6 6-6 6"/>
                        </svg>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Active packages / promos — compact strip ── */}
      {packages.length > 0 && (
        <div className="pa-deals">
          <div className="pa-container">
            <div className="pa-deals__strip">
              <span className="pa-deals__label">{lang === "en" ? "Deals" : "Akcije"}</span>
              {packages.map((pkg) => {
                const daysLeft = Math.ceil(
                  (new Date(pkg.validTo + "T23:59:59").getTime() - Date.now()) / 86400000
                );
                const urgent = daysLeft <= 5;
                const discount = pkg.discountPercent != null ? `−${pkg.discountPercent}%` : null;
                return (
                  <button
                    key={pkg._id}
                    className={`pa-deal${urgent ? " pa-deal--urgent" : ""}`}
                    onClick={() => {
                      if (pkg.apartmentId) {
                        const base = `/apartments/public/${pkg.apartmentId}?clientId=${clientId}`;
                        const qs = checkIn && checkOut ? `&checkIn=${checkIn}&checkOut=${checkOut}` : "";
                        navigate(base + qs + (refCode ? `&ref=${refCode}` : ""));
                      } else {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                  >
                    {discount && <span className="pa-deal__pct">{discount}</span>}
                    <span className="pa-deal__name">{pkg.name}</span>
                    <span className="pa-deal__till">
                      {lang === "en" ? "until" : "do"} {fmtDisplayDate(pkg.validTo)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer className="pa-foot">
        <div className="pa-container pa-foot__inner">
          <div className="pa-foot__brand">
            <span
              className="pa-nav__logo"
              style={{ background: apartments[0]?.coverGradient || "linear-gradient(135deg, var(--pa-accent), var(--pa-accent2))" }}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/>
              </svg>
            </span>
            <div>
              <div className="pa-foot__brand-name">{lang === "en" ? "Our apartments" : "Naši apartmani"}</div>
            </div>
          </div>
          <div className="pa-foot__copy">
            <span>Powered by FixmanHR · Sva prava zadržana</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicApartments;
